import type {Floor,Point} from './carpet-run';
import {CarpetLayout} from '@artemis-group/aiw-contracts';

/** A measured proposal drawn over the shared canvas, separate from a saved physical-cut review. */
export type CarpetCandidate={
 ref:string;sourceSave:number;rollWidthM:number;measuredFloorM2:number;stairBlankM2:number;
 floors:Array<{name:string;direction:'leftRight';cuts:Array<{label:string;lengthM:number;rooms:string[];footprint:Point[]}>;
  stairOffcuts:Array<{label:string;footprint:Point[]}>;seams:Array<{a:Point;b:Point}>}>;
 checks:string[];
};

const points=(pairs:number[][]):Point[]=>pairs.map(([x,y])=>({x:x!,y:y!}));

/** Jacky's four-cut Arncliffe teaching layout on the native save-13 image coordinates. */
export const ARNCLIFFE_CANDIDATE:CarpetCandidate={
 ref:'P-0926-10231',sourceSave:13,rollWidthM:3.66,measuredFloorM2:80.77,stairBlankM2:8.01,
 floors:[
  {name:'Entry level',direction:'leftRight',cuts:[
   {label:'1 · Bedroom 1 + BIRs + ensuite entry',lengthM:5.15,rooms:['Bedroom 1'],footprint:points([[1063,527],[1063,409],[1035,409],[839,409],[839,549],[1035,549],[1035,527]])},
   {label:'2 · Bedroom 2 + BIR + hallway + hall robe',lengthM:8.74,rooms:['Bedroom 2','Hallway'],footprint:points([[1122,633],[1152,633],[1152,682],[1235,682],[1235,633],[1235,549],[1157,549],[1157,527],[1035,527],[1035,549],[986,549],[839,549],[839,682],[986,682],[1011,682],[1011,595],[1122,595]])},
  ],stairOffcuts:[{label:'2A · stairs 1–10',footprint:points([[1011,595],[1122,595],[1122,633],[1152,633],[1152,682],[1011,682]])}],seams:[{a:{x:986,y:549},b:{x:1035,y:549}}]},
  {name:'Upper level',direction:'leftRight',cuts:[
   {label:'3 · Living lower band',lengthM:9.65,rooms:['Living'],footprint:points([[712,175],[1154,175],[1154,340],[712,340],[712,289],[749,289],[749,253],[932,253],[932,175]])},
   {label:'4 · Living upper band',lengthM:4.99,rooms:['Living'],footprint:points([[992,46],[1154,46],[1154,175],[932,175],[932,122],[992,122]])},
  ],stairOffcuts:[{label:'3A · stairs 11–16',footprint:points([[712,175],[932,175],[932,253],[749,253],[749,289],[712,289]])}],seams:[{a:{x:932,y:175},b:{x:1154,y:175}}]},
 ],
 checks:[
  'Hall robe depth is not saved as a laser reading; confirm it fits Cut 2 across the 3.66 m width.',
  'Bedroom 1 and hallway laser outlines have 0.25 m and 1.41 m closure gaps.',
  'Sixteen stair blank envelopes fit the shown offcuts, but winder templates, lip wrapping and nosing remain unmeasured.',
  'The private Proposal measured 79.86 m² when this view was built; save 13 measured 80.77 m². Reconcile before customer release.',
 ],
};

export function candidateSummary(candidate:CarpetCandidate){
 const cutLengthM=Number(candidate.floors.flatMap(f=>f.cuts).reduce((sum,cut)=>sum+cut.lengthM,0).toFixed(2));
 const wholeLm=Math.ceil(cutLengthM);
 const purchasedM2=Number((wholeLm*candidate.rollWidthM).toFixed(2));
 const wasteM2=Number((purchasedM2-candidate.measuredFloorM2-candidate.stairBlankM2).toFixed(2));
 return {cutLengthM,wholeLm,purchasedM2,wasteM2};
}

export function candidateFor(ref:string|undefined,floors:Floor[]):CarpetCandidate|null{
 if(ref!==ARNCLIFFE_CANDIDATE.ref)return null;
 if(!ARNCLIFFE_CANDIDATE.floors.every(f=>floors.some(source=>source.name===f.name&&source.areas.some(a=>a.finish?.toLowerCase()==='carpet'))))return null;
 return ARNCLIFFE_CANDIDATE;
}

/** Preserve the cut arithmetic in a private Proposal draft without claiming a site-checked review. */
export function candidateProposalLayout(candidate:CarpetCandidate,revisionId:string){
 return CarpetLayout.parse({source_revision_id:revisionId,review_status:'provisional',roll_width_m:candidate.rollWidthM,
  cuts:candidate.floors.flatMap(floor=>floor.cuts.map(cut=>({floor:floor.name,label:cut.label,length_m:cut.lengthM,allowance_m:0.1,direction:floor.direction,rooms:cut.rooms}))),
  floor_used_m2:null,stair_used_m2:null,
  notes:`Drawn from canvas save ${candidate.sourceSave}. Site checks pending: ${candidate.checks.join(' ')}`});
}
